import { alpha, keyframes, type SxProps, type Theme } from "@mui/material/styles";

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const enter = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const root =
  (overlay: boolean): SxProps<Theme> =>
  (theme) => ({
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    px: 2,
    py: { xs: 3, md: 4 },
    bgcolor: "background.default",
    backgroundImage: `radial-gradient(ellipse 60% 45% at 50% 45%, ${alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.16 : 0.09,
    )}, transparent 70%)`,
    ...(overlay
      ? { position: "fixed", inset: 0, zIndex: theme.zIndex.modal + 1, overflowY: "auto" }
      : {}),
  });

export const logoLink: SxProps<Theme> = {
  display: "inline-flex",
  borderRadius: 1,
  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 4 },
};

export const logo: SxProps<Theme> = {
  height: { xs: 28, md: 32 },
  width: "auto",
  display: "block",
};

export const content: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  maxWidth: 460,
  width: "100%",
  py: 3,
  animation: `${enter} 480ms cubic-bezier(0.2, 0.7, 0.2, 1) both`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

export const image: SxProps<Theme> = {
  width: { xs: "min(260px, 72vw)", md: 360 },
  aspectRatio: "1 / 1",
  objectFit: "contain",
  mb: { xs: 1, md: 2 },
  userSelect: "none",
  pointerEvents: "none",
  animation: `${float} 5s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

export const title: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: "1.375rem", md: "1.625rem" },
  color: "text.primary",
  mb: 1.25,
};

export const description: SxProps<Theme> = {
  color: "text.secondary",
  lineHeight: 1.9,
  maxWidth: 380,
};

export const actions: SxProps<Theme> = {
  mt: 3.5,
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  gap: 1.5,
};

export const footer: SxProps<Theme> = {
  color: "text.disabled",
  fontSize: "0.75rem",
};
